import { CursorArrowRaysIcon, TrashIcon } from '@heroicons/react/20/solid'
import { useEffect, useMemo, useState } from 'react'
import { Handle, NodeProps, Position, useUpdateNodeInternals } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { Source } from '@ui/__generated__/graphql'
import {
  BrowserCodeIcon,
  DatableIcon,
  HurricaneTwisterIcon,
  StartPointDiamondIcon,
  VerticalMenuIcon,
} from '@ui/components/icons'
import { sourceTypeToLogoAssetSlug } from '@ui/constants/sources'
import { NodeTypeEnum, SourceHandleEnum, TargetHandleEnum } from '@ui/interfaces'
import { useLogoPathBySlug } from '@ui/lib/hooks/useLogoPathBySlug'
import { convertBytesToText } from '@ui/lib/utils'
import { useSaveUpdateStatusStore } from '@ui/stores'
import clsx from 'clsx'

interface NodeContentProps {
  node: NodeProps
  nodeIsSelected: boolean
  deleteNode: (node: NodeProps, isDemoMode: boolean) => void
  isDemoMode: boolean
  hasChildren: boolean
  sources: Source[]
}

export const NodeContent = ({
  node,
  nodeIsSelected,
  deleteNode,
  isDemoMode,
  hasChildren,
  sources,
}: NodeContentProps) => {
  const updateNodeInternals = useUpdateNodeInternals()
  const logoPathBySlug = useLogoPathBySlug([node.data.exportType])
  const logoPathBySourceType = useLogoPathBySlug(sources.map((source) => sourceTypeToLogoAssetSlug[source.type]))

  const { assignedSourceIds } = useSaveUpdateStatusStore(
    useShallow((state) => ({
      assignedSourceIds: state.assignedSourceIds,
    }))
  )

  const formatExport = (exportType?: string) => {
    if (!exportType) return ''
    return exportType
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const [showMenu, setShowMenu] = useState(false)

  useEffect(() => {
    updateNodeInternals(node.id)
  }, [updateNodeInternals, node.id])

  // close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent, nodeId: string) => {
      if (showMenu && e.target instanceof HTMLElement && !e.target.closest(`#menu-container-${nodeId}`)) {
        setShowMenu(false)
      }
    }

    const handleClickOutsideWrapper = (e: MouseEvent) => handleClickOutside(e, node.id)
    document.addEventListener('click', handleClickOutsideWrapper)
    return () => {
      document.removeEventListener('click', handleClickOutsideWrapper)
    }
  }, [showMenu, node.id])

  const steps = useMemo(() => {
    if (!node.data.steps) {
      return []
    }

    return node.data.steps
  }, [node.data.steps])

  const outputSize = useMemo(() => {
    if (!node.data.size) return ''

    const size = node.data.size
    return convertBytesToText(size.log + size.span + size.metric)
  }, [node.data.size])

  const hideNodeOptions =
    node.data.isMinimap || (!hasChildren && node.type !== NodeTypeEnum.EXPORT) || node.type === NodeTypeEnum.FIREHOSE

  const nodeHandles = (
    <>
      <Handle
        id={TargetHandleEnum.TARGET_PLACEHOLDER}
        className="invisible"
        type="target"
        position={Position.Left}
        isConnectable={false}
      />
      <Handle
        id={SourceHandleEnum.SOURCE_PLACEHOLDER}
        className="invisible"
        type="source"
        position={Position.Right}
        isConnectable={false}
      />

      <Handle
        id={TargetHandleEnum.TARGET_LOGS}
        className="invisible fixed left-[5rem] top-1"
        type="target"
        position={Position.Top}
        isConnectable={false}
      />
      <Handle
        id={SourceHandleEnum.SOURCE_LOGS}
        className="invisible fixed bottom-1 left-[10rem]"
        type="source"
        position={Position.Bottom}
        isConnectable={false}
      />

      <Handle
        id={TargetHandleEnum.TARGET_SPANS}
        className="invisible fixed left-[4.8rem] top-1"
        type="target"
        position={Position.Top}
        isConnectable={false}
      />
      <Handle
        id={SourceHandleEnum.SOURCE_SPANS}
        className="invisible fixed bottom-5 left-[9.5rem]"
        type="source"
        position={Position.Bottom}
        isConnectable={false}
      />

      <Handle
        id={TargetHandleEnum.TARGET_METRICS}
        className="invisible fixed left-[5.85rem] top-1"
        type="target"
        position={Position.Top}
        isConnectable={false}
      />
      <Handle
        id={SourceHandleEnum.SOURCE_METRICS}
        className="invisible fixed bottom-10 left-[7.75rem]"
        type="source"
        position={Position.Bottom}
        isConnectable={false}
      />
    </>
  )

  const nodeOptionsMenu = (
    <div
      className="group relative z-40 flex size-5 items-center justify-end"
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        setShowMenu(!showMenu)
      }}>
      <div
        className={clsx('flex size-5 cursor-pointer items-center justify-center rounded', {
          'bg-gray-200/60': showMenu && node.type !== NodeTypeEnum.PLACEHOLDER,
          'hover:bg-gray-200/60': !showMenu && node.type !== NodeTypeEnum.PLACEHOLDER,
          'bg-secondary-400/60': showMenu && node.type === NodeTypeEnum.PLACEHOLDER,
          'hover:bg-secondary-400/60': !showMenu && node.type === NodeTypeEnum.PLACEHOLDER,
        })}>
        <VerticalMenuIcon
          viewBox="0 0 16 16"
          className={clsx('size-4 stroke-gray-600', {
            'stroke-secondary-500': showMenu && node.type !== NodeTypeEnum.PLACEHOLDER,
            'group-hover:stroke-secondary-500': !showMenu && node.type !== NodeTypeEnum.PLACEHOLDER,
            'stroke-gray-500': showMenu && node.type === NodeTypeEnum.PLACEHOLDER,
            'group-hover:stroke-gray-500': !nodeIsSelected && node.type === NodeTypeEnum.PLACEHOLDER,
          })}
        />
      </div>

      {showMenu && (
        <div
          className="absolute right-0 top-6 z-40 w-24 rounded-md shadow-lg ring-1 ring-gray-200"
          id={`menu-container-${node.id}`}>
          <div
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="options-menu"
            className="flex flex-col space-y-2 rounded-md bg-white">
            <div
              className="flex flex-row items-center justify-start space-x-2 rounded-md p-2 transition-all duration-150 ease-in-out hover:bg-gray-100/60"
              onClick={() => {
                deleteNode(node, isDemoMode)
              }}>
              <div className="flex size-4 items-center justify-center rounded-full bg-primary-50">
                <TrashIcon className="size-2 text-primary-600" aria-hidden="true" />
              </div>
              <p className="text-3xs font-semibold text-primary-600">Delete Node</p>
            </div>

            {/* <button
          onClick={() => {
            deleteNode(node, isDemoMode)
          }}
          className="flex w-full items-center rounded bg-primary-50 p-4 text-[11px] text-primary-500 hover:bg-primary-200"
          role="menuitem">
          <TrashIcon className="mr-3 h-3 w-3" aria-hidden="true" />
          Delete
        </button> */}
            {/* Add more menu items here if needed */}
          </div>
        </div>
      )}
    </div>
  )

  const renderContent = () => {
    const renderIcon = () => {
      switch (node.type) {
        case NodeTypeEnum.FIREHOSE:
          return <DatableIcon color="fill-primary-400" height={12} width={12} />
        case NodeTypeEnum.CODE:
          return <BrowserCodeIcon className="stroke-gray-900" height={12} width={12} />
        case NodeTypeEnum.TRANSFORM:
          return <HurricaneTwisterIcon className="stroke-gray-900" height={12} width={12} />
        case NodeTypeEnum.EXPORT:
          return <StartPointDiamondIcon className="stroke-gray-900" height={12} width={12} />
        default:
          return null
      }
    }

    const renderDescription = () => {
      if (node.type === NodeTypeEnum.FIREHOSE) {
        return 'Sources'
      }
      return node.data.description || 'No description'
    }

    const renderInnerContent = () => {
      const assignedSources = sources.filter((source) => assignedSourceIds.has(source.id))
      switch (node.type) {
        case NodeTypeEnum.FIREHOSE:
          return (
            <div className="flex flex-wrap space-y-1">
              {assignedSources.length ? (
                <>
                  {assignedSources.map((source) => (
                    <div
                      key={source.id}
                      className="flex flex-row items-center space-x-1 rounded border border-gray-300 p-1">
                      <img
                        src={logoPathBySourceType[sourceTypeToLogoAssetSlug[source.type]]}
                        alt={source.type}
                        className="size-4"
                      />
                      {/* Rename sources with the name Unidentified Sources to something more consumer friendly */}
                      <p className="text-xs font-medium">
                        {source.name === 'Unidentified Sources' ? 'Unspecified OTel' : source.name}
                      </p>
                    </div>
                  ))}
                </>
              ) : (
                <p className="text-2xs font-medium">No sources assigned</p>
              )}
            </div>
          )
        case NodeTypeEnum.CODE:
          return <p className="line-clamp-2 break-words text-2xs font-normal">{renderDescription()}</p>
        case NodeTypeEnum.TRANSFORM:
          return (
            <div className="flex size-full flex-col justify-between space-y-1">
              <p className="line-clamp-2 break-words text-2xs font-normal">{renderDescription()}</p>
              <p className="w-fit rounded-md bg-gray-200 px-1 text-3xs font-medium">{steps.length} steps</p>
            </div>
          )
        // return <p className="truncate text-xs font-medium">View {steps.length} steps</p>
        case NodeTypeEnum.EXPORT:
          return (
            <div className="flex flex-row items-center space-x-1">
              {logoPathBySlug[node.data.exportType] ? (
                <img src={logoPathBySlug[node.data.exportType]} alt={node.data.exportType} className="size-6" />
              ) : (
                <div className="size-6 rounded-full bg-gray-200" />
              )}
              <p className="text-2xs">{formatExport(node.data.exportType) || 'No destination selected'}</p>
            </div>
          )
        default:
          return null
      }
    }

    switch (node.type) {
      case NodeTypeEnum.FIREHOSE:
      case NodeTypeEnum.CODE:
      case NodeTypeEnum.TRANSFORM:
      case NodeTypeEnum.EXPORT:
        return (
          <div
            className={clsx(
              'group flex flex-col rounded-md border border-gray-200 bg-white p-1 transition-all duration-150 group-hover:border-secondary-500',
              {
                'border-secondary-500': nodeIsSelected,
              }
            )}>
            <div className="flex flex-row items-center justify-start space-x-1">
              <div className="flex w-fit flex-row items-center space-x-1 rounded p-1">{renderIcon()}</div>

              <div className="flex flex-row p-1">
                <p className="text-2xs font-medium leading-tight text-gray-800">{outputSize}/min out</p>
              </div>
            </div>

            <div
              className={clsx(
                'flex h-auto min-h-11 max-w-60 items-center justify-start overflow-hidden rounded-md pl-2 pr-5',
                {
                  'h-20': node.type !== NodeTypeEnum.FIREHOSE,
                }
              )}>
              {renderInnerContent()}
            </div>

            <div
              className={clsx('absolute right-1 top-2 group-hover:block', {
                hidden: !showMenu && !nodeIsSelected,
              })}>
              {!hideNodeOptions && nodeOptionsMenu}
            </div>
          </div>
        )

      case NodeTypeEnum.PLACEHOLDER:
        return (
          <div
            className={clsx(
              'relative flex h-20 flex-row items-center justify-center space-x-1 rounded-md border border-dashed border-gray-200',
              'transition-all duration-150 group-hover:border-secondary-500 group-hover:bg-secondary-200',
              {
                'border-secondary-500 bg-secondary-200': nodeIsSelected,
                'bg-secondary-50': !nodeIsSelected,
              }
            )}>
            <CursorArrowRaysIcon className="size-5 text-gray-800" />
            <p className="text-xs font-normal">Add node</p>

            <div
              className={clsx('absolute right-2 top-2 group-hover:block', {
                hidden: !showMenu && !nodeIsSelected,
              })}>
              {!hideNodeOptions && nodeOptionsMenu}
            </div>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="relative w-full">
      {renderContent()}
      {nodeHandles}
    </div>
  )
}
